import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../auth";
import api from "../api";

const MONTHS=["January","February","March","April","May","June","July","August","September","October","November","December"];
const today=()=>new Date().toISOString().slice(0,10);

export default function Timesheet() {
  const { me } = useAuth();
  const [searchParams,setSearchParams]=useSearchParams();
  const [view,    setView]    = useState("day");
  const [date,    setDate]    = useState(today());
  const [clients, setClients] = useState([]);
  const [projects,setProjects]= useState([]);
  const [jobs,    setJobs]    = useState([]);
  const [rows,    setRows]    = useState([{id:1,client:"",project:"",job:"",description:"",hours:""}]);
  const [saveMsg, setSaveMsg] = useState({text:"",ok:true});
  const [calData, setCalData] = useState({});
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth,setCalMonth]= useState(new Date().getMonth());
  const [empList, setEmpList] = useState([]);
  const [selEmp,  setSelEmp]  = useState(searchParams.get("emp")||"");
  const isAdmin = me?.role==="admin"||me?.role==="hr";
  const rowId=useRef(2);

  useEffect(()=>{
    Promise.all([
      api.get("/portal/timesheet/clients"),
      api.get("/portal/timesheet/projects"),
      api.get("/portal/timesheet/jobs"),
    ]).then(([c,p,j])=>{setClients(c.clients||[]);setProjects(p.projects||[]);setJobs(j.jobs||[]);});
    if(isAdmin) api.get("/portal/team").then(d=>setEmpList(d.members||[])).catch(()=>{});
  },[]);

  useEffect(()=>{const e=searchParams.get("emp");if(e)setSelEmp(e);},[searchParams]);

  useEffect(()=>{
    if(view!=="day") return;
    const empParam = selEmp&&selEmp!==me?.employee_id?`&emp=${selEmp}`:"";
    api.get(`/portal/timesheet/entries?date=${date}${empParam}`)
      .then(d=>{
        if(d.entries?.length){
          setRows(d.entries.map((e,i)=>({id:i+1,client:e.client_id||"",project:e.project_id||"",
            job:e.job_id||"",description:e.description,hours:e.hours})));
          rowId.current=d.entries.length+1;
        } else {
          setRows([{id:1,client:"",project:"",job:"",description:"",hours:""},
                   {id:2,client:"",project:"",job:"",description:"",hours:""},
                   {id:3,client:"",project:"",job:"",description:"",hours:""}]);
          rowId.current=4;
        }
      }).catch(()=>{});
  },[date,view,selEmp]);

  useEffect(()=>{
    if(view!=="cal"&&view!=="week") return;
    const month=`${calYear}-${String(calMonth+1).padStart(2,"0")}`;
    const empParam=selEmp&&selEmp!==me?.employee_id?`&emp=${selEmp}`:"";
    api.get(`/portal/timesheet/summary?month=${month}${empParam}`)
      .then(d=>setCalData(d.days||{})).catch(()=>{});
  },[calYear,calMonth,view,selEmp]);

  const total=rows.reduce((s,r)=>s+(parseFloat(r.hours)||0),0);
  const fmt=n=>{const h=Math.floor(n),m=Math.round((n-h)*60);return `${h}:${String(m).padStart(2,"0")}`;};
  const updateRow=(id,field,val)=>setRows(rs=>rs.map(r=>r.id===id?({...r,[field]:val,...(field==="client"?{project:""}:{})}):r));

  const canEdit = !selEmp || selEmp===me?.employee_id || me?.role==="admin";

  const save=async()=>{
    if (!canEdit) { setSaveMsg({text:"You can only view this employee's timesheet.",ok:false}); return; }
    const validRows=rows.filter(r=>parseFloat(r.hours)>0).map(r=>({
      client_id:r.client?parseInt(r.client):null,project_id:r.project?parseInt(r.project):null,
      job_id:r.job?parseInt(r.job):null,description:r.description,hours:parseFloat(r.hours),
    }));
    if(!validRows.length){setSaveMsg({text:"Add at least one row with hours.",ok:false});return;}
    try{
      const res=await api.post("/portal/timesheet/entries",{date,rows:validRows});
      setSaveMsg({text:res.warning||`✓ Saved — ${res.total_hours}h logged`,ok:!res.warning});
    }catch(e){setSaveMsg({text:e.message,ok:false});}
  };

  const navDate=d=>{const dt=new Date(date);dt.setDate(dt.getDate()+d);setDate(dt.toISOString().slice(0,10));};
  const calNav=d=>{let m=calMonth+d,y=calYear;if(m<0){m=11;y--;}if(m>11){m=0;y++;}setCalMonth(m);setCalYear(y);};
  const dayBg=(ds,isWE)=>{
    const h=calData[ds]||0;
    if(isWE)return{bg:"#F8FAFC",color:"var(--muted)"};
    if(h>=8)return{bg:"#DCFCE7",color:"#166534"};
    if(h>0)return{bg:"#FEF3C7",color:"#92400e"};
    if(ds<=today())return{bg:"#FEE2E2",color:"#991B1B"};
    return{bg:"var(--soft)",color:"var(--ink)"};
  };

  return (
    <div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:18,flexWrap:"wrap",gap:10}}>
        <div><h2 className="page-title">Timesheet</h2><p className="page-sub">Log your daily work hours</p></div>
        <div className="seg-ctrl">
          {[["day","📋 Daily Log"],["cal","📅 Calendar"],["week","📊 Weekly"]].map(([v,l])=>(
            <button key={v} className={`seg-btn ${view===v?"active":""}`} onClick={()=>setView(v)}>{l}</button>
          ))}
        </div>
      </div>

      {/* Employee selector (admin) */}
      {isAdmin&&(
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
          <label style={{fontSize:13,color:"var(--muted)",margin:0}}>Employee:</label>
          <select className="inp" style={{width:"auto",minWidth:200}} value={selEmp}
            onChange={e=>{setSelEmp(e.target.value);setSearchParams(e.target.value?{emp:e.target.value}:{});}}>
            <option value="">Myself ({me?.employee_id})</option>
            {empList.map(e=><option key={e.employee_id} value={e.employee_id}>{e.name} ({e.employee_id})</option>)}
          </select>
        </div>
      )}

      {/* Date nav */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <button className="btn secondary" style={{padding:"6px 12px"}} onClick={()=>navDate(-1)}>‹</button>
        <input type="date" value={date} onChange={e=>setDate(e.target.value)}
          style={{border:"1px solid var(--line)",borderRadius:8,padding:"8px 12px",fontSize:14,fontFamily:"inherit"}}/>
        <button className="btn secondary" style={{padding:"6px 12px"}} onClick={()=>navDate(1)}>›</button>
        <button className="btn secondary" style={{padding:"6px 14px",fontSize:13}} onClick={()=>setDate(today())}>Today</button>
        {view==="day"&&total>0&&<span style={{marginLeft:"auto",fontWeight:600,color:"var(--accent)",fontSize:13}}>Daily total: {fmt(total)}</span>}
      </div>

      {/* Daily Log */}
      {view==="day"&&(
        <>
          <div className="card" style={{overflowX:"auto"}}>
            <table className="tbl">
              <thead><tr>
                <th style={{width:30}}>#</th>
                <th style={{minWidth:150}}>Client</th><th style={{minWidth:170}}>Project</th>
                <th style={{minWidth:140}}>Job</th><th>Description</th>
                <th style={{width:90}}>Hours</th><th style={{width:36}}/>
              </tr></thead>
              <tbody>
                {rows.map((row,i)=>(
                  <tr key={row.id}>
                    <td style={{color:"var(--muted)"}}>{i+1}</td>
                    <td style={{padding:"4px 8px"}}>
                      <select className="inp" value={row.client} disabled={!canEdit}
                        onChange={e=>updateRow(row.id,"client",e.target.value)}>
                        <option value="">— Client —</option>
                        {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </td>
                    <td style={{padding:"4px 8px"}}>
                      <select className="inp" value={row.project} disabled={!canEdit}
                        onChange={e=>updateRow(row.id,"project",e.target.value)}>
                        <option value="">— Project —</option>
                        {projects.filter(p=>!row.client||String(p.client_id)===String(row.client))
                          .map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </td>
                    <td style={{padding:"4px 8px"}}>
                      <select className="inp" value={row.job} disabled={!canEdit}
                        onChange={e=>updateRow(row.id,"job",e.target.value)}>
                        <option value="">— Job —</option>
                        {jobs.map(j=><option key={j.id} value={j.id}>{j.name}</option>)}
                      </select>
                    </td>
                    <td style={{padding:"4px 8px"}}>
                      <input className="inp" value={row.description} disabled={!canEdit}
                        placeholder="What did you work on?"
                        onChange={e=>updateRow(row.id,"description",e.target.value)}/>
                    </td>
                    <td style={{padding:"4px 8px"}}>
                      <input className="inp" type="number" min="0" max="24" step="0.25"
                        value={row.hours} placeholder="0.00" disabled={!canEdit}
                        style={{textAlign:"right"}} onChange={e=>updateRow(row.id,"hours",e.target.value)}/>
                    </td>
                    <td style={{textAlign:"center"}}>
                      {canEdit&&<button onClick={()=>setRows(rs=>rs.filter(r=>r.id!==row.id))}
                        style={{background:"none",border:"none",cursor:"pointer",color:"#ef4444",fontSize:18}}>×</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{borderTop:"2px solid var(--line)"}}>
                  <td colSpan={5} style={{padding:"10px 8px"}}>
                    {canEdit&&<button onClick={()=>{const id=rowId.current++;setRows(rs=>[...rs,{id,client:"",project:"",job:"",description:"",hours:""}]);}}
                      style={{background:"none",border:"1px dashed var(--accent)",color:"var(--accent)",
                        borderRadius:7,padding:"6px 14px",cursor:"pointer",fontSize:13}}>+ Add Row</button>}
                  </td>
                  <td style={{padding:"10px 8px",fontWeight:700,fontSize:16,color:total>0?"var(--accent)":"var(--muted)"}}>{fmt(total)}</td>
                  <td/>
                </tr>
              </tfoot>
            </table>
          </div>
          {total>0&&total<8&&<div style={{marginTop:10,padding:"10px 14px",background:"#FEF3C7",borderRadius:8,color:"#92400E",fontSize:13}}>⚠ Minimum 8 hours required for a full working day.</div>}
          {canEdit&&<div style={{marginTop:14,display:"flex",gap:10,alignItems:"center"}}>
            <button className="btn" onClick={save}>Save Timesheet</button>
            {saveMsg.text&&<span style={{fontSize:13,color:saveMsg.ok?"var(--ok)":"var(--danger)"}}>{saveMsg.text}</span>}
          </div>}
        </>
      )}

      {/* Calendar */}
      {view==="cal"&&(
        <div className="card">
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
            <button onClick={()=>calNav(-1)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer"}}>‹</button>
            <b style={{fontSize:15}}>{MONTHS[calMonth]} {calYear}</b>
            <button onClick={()=>calNav(1)}  style={{background:"none",border:"none",fontSize:22,cursor:"pointer"}}>›</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:8}}>
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d=><div key={d} style={{textAlign:"center",fontSize:11,fontWeight:600,color:"var(--muted)",padding:"4px 0"}}>{d}</div>)}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
            {Array(new Date(calYear,calMonth,1).getDay()).fill(null).map((_,i)=><div key={`e${i}`}/>)}
            {Array(new Date(calYear,calMonth+1,0).getDate()).fill(0).map((_,i)=>{
              const day=i+1;
              const ds=`${calYear}-${String(calMonth+1).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
              const dow=(new Date(calYear,calMonth,1).getDay()+i)%7;
              const isWE=dow===0||dow===6;
              const {bg,color}=dayBg(ds,isWE);
              const h=calData[ds]||0;
              return(
                <div key={day} onClick={()=>{setDate(ds);setView("day");}}
                  style={{borderRadius:8,padding:"8px 4px",textAlign:"center",cursor:"pointer",
                    background:bg,color:color,border:`1px solid ${ds===today()?"var(--accent)":"transparent"}`}}>
                  <div style={{fontWeight:600,fontSize:14}}>{day}</div>
                  {h>0&&<div style={{fontSize:10,marginTop:2}}>{h}h</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Weekly */}
      {view==="week"&&(()=>{
        const d=new Date(date),dow=d.getDay();
        const mon=new Date(d);mon.setDate(d.getDate()-(dow===0?6:dow-1));
        const days7=Array(7).fill(0).map((_,i)=>{const dd=new Date(mon);dd.setDate(mon.getDate()+i);return dd.toISOString().slice(0,10);});
        const DN=["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
        const weekTotal=days7.reduce((s,ds)=>s+(calData[ds]||0),0);
        return(
          <div className="card" style={{overflowX:"auto"}}>
            <table className="tbl">
              <thead><tr>
                <th>Metric</th>
                {days7.map((ds,i)=><th key={ds} style={{textAlign:"center",color:ds===today()?"var(--accent)":"var(--muted)"}}>
                  {DN[i]}<br/><span style={{fontWeight:400,fontSize:11}}>{ds.slice(5)}</span></th>)}
                <th style={{textAlign:"center"}}>Total</th>
              </tr></thead>
              <tbody>
                <tr>
                  <td style={{fontWeight:600}}>Hours Logged</td>
                  {days7.map((ds,i)=>{const h=calData[ds]||0;const isWE=i>=5;
                    const color=isWE?"var(--muted)":h>=8?"#166534":h>0?"#92400e":"var(--muted)";
                    return<td key={ds} style={{textAlign:"center",fontWeight:600,color}}>{h||"—"}</td>;
                  })}
                  <td style={{textAlign:"center",fontWeight:700,color:"var(--accent)"}}>{weekTotal}h</td>
                </tr>
                <tr>
                  <td style={{color:"var(--muted)",fontSize:13}}>Status</td>
                  {days7.map((ds,i)=>{const h=calData[ds]||0;const isWE=i>=5;
                    let label="",color="var(--muted)";
                    if(isWE){label="Weekend";}else if(h>=8){label="✓ Complete";color="#166534";}
                    else if(h>0){label="⚠ Partial";color="#92400e";}
                    else if(ds<=today()){label="✗ Missing";color="#991B1B";}
                    return<td key={ds} style={{textAlign:"center",fontSize:12,color}}>{label}</td>;
                  })}
                  <td style={{textAlign:"center",fontSize:12,color:weekTotal>=40?"#166534":"#92400e"}}>{weekTotal>=40?"✓ 40h+":"Target: 40h"}</td>
                </tr>
                <tr><td colSpan={9} style={{textAlign:"center",paddingTop:8}}>
                  {days7.slice(0,5).map((ds,i)=>(
                    <button key={ds} onClick={()=>{setDate(ds);setView("day");}}
                      style={{margin:2,background:"var(--soft)",border:"1px solid var(--line)",
                        borderRadius:6,padding:"5px 10px",cursor:"pointer",fontSize:12}}>{DN[i]} log</button>
                  ))}
                </td></tr>
              </tbody>
            </table>
          </div>
        );
      })()}
    </div>
  );
}
